import { randomUUID } from "~/lib/utils";

import type { CustomVaultFolder } from "./vaultFolders";

const STORAGE_PREFIX = "morrow-vault-custom-folders";

export function customVaultFoldersStorageKey(userId: string | undefined): string {
  const scope = userId?.trim() || "anonymous";
  return `${STORAGE_PREFIX}:${scope}`;
}

export function loadCustomVaultFolders(storageKey: string): CustomVaultFolder[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((entry) => {
        if (!entry || typeof entry !== "object") return null;
        const rec = entry as Record<string, unknown>;
        if (typeof rec.id !== "string" || typeof rec.name !== "string") return null;
        return {
          id: rec.id,
          name: rec.name.trim(),
          createdAt: typeof rec.createdAt === "string" ? rec.createdAt : new Date().toISOString(),
        } satisfies CustomVaultFolder;
      })
      .filter((entry): entry is CustomVaultFolder => entry !== null && entry.name.length > 0);
  } catch {
    return [];
  }
}

export function saveCustomVaultFolders(
  storageKey: string,
  folders: readonly CustomVaultFolder[],
): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(storageKey, JSON.stringify(folders));
}

export function createCustomVaultFolder(name: string): CustomVaultFolder {
  const trimmed = name.trim();
  return {
    id: randomUUID(),
    name: trimmed,
    createdAt: new Date().toISOString(),
  };
}

const ASSIGNMENTS_PREFIX = "morrow-vault-item-folders";

export function vaultItemAssignmentsStorageKey(userId: string | undefined): string {
  const scope = userId?.trim() || "anonymous";
  return `${ASSIGNMENTS_PREFIX}:${scope}`;
}

export type VaultItemFolderAssignments = Readonly<Record<string, string>>;

export function loadVaultItemAssignments(storageKey: string): VaultItemFolderAssignments {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    const next: Record<string, string> = {};
    for (const [itemId, folderId] of Object.entries(parsed)) {
      if (typeof itemId === "string" && typeof folderId === "string" && folderId.trim()) {
        next[itemId] = folderId.trim();
      }
    }
    return next;
  } catch {
    return {};
  }
}

export function saveVaultItemAssignments(
  storageKey: string,
  assignments: VaultItemFolderAssignments,
): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(storageKey, JSON.stringify(assignments));
}

export function setVaultItemFolderAssignment(
  assignments: VaultItemFolderAssignments,
  itemId: string,
  folderId: string | null,
): VaultItemFolderAssignments {
  const next = { ...assignments };
  if (folderId) next[itemId] = folderId;
  else delete next[itemId];
  return next;
}

export function removeVaultItemAssignmentsForFolder(
  assignments: VaultItemFolderAssignments,
  folderId: string,
): VaultItemFolderAssignments {
  const next: Record<string, string> = {};
  for (const [itemId, assignedFolderId] of Object.entries(assignments)) {
    if (assignedFolderId !== folderId) next[itemId] = assignedFolderId;
  }
  return next;
}

export function removeVaultItemAssignment(
  assignments: VaultItemFolderAssignments,
  itemId: string,
): VaultItemFolderAssignments {
  return setVaultItemFolderAssignment(assignments, itemId, null);
}
